import { z } from "zod";
import { BASELINE_MODES, GATE_CONDITION_IDS, GATE_LEVELS } from "./config.js";

/**
 * `iamu.gate-result/v1` — the versioned, machine-readable gate result
 * (spec `quality-gate`, "Machine-readable result contract"). Consumed by
 * CI over REST and by the GitHub check run. The published JSON Schema
 * (`packages/shared/schemas/gate-result.v1.json`) is generated from this
 * Zod schema by `pnpm --filter @iamu/shared gen:gate-schema`; a test
 * fails when the two drift.
 *
 * Additive changes (new optional fields) stay v1. Renames, removals or
 * changed meanings bump `schema_version`.
 */

export const GATE_RESULT_SCHEMA_URL =
  "https://iamu.dev/schemas/gate-result/v1.json";
export const GATE_EVALUATOR_VERSION = "1.0.0";
export const GATE_ITEMS_CAP = 500;

export const GATE_STATUSES = [
  "passed",
  "warned",
  "failed",
  "inconclusive",
  "error",
] as const;
export type GateStatus = (typeof GATE_STATUSES)[number];

/** Bucket order = sort order of `items` and tab order on the build page. */
export const GATE_BUCKETS = [
  "new",
  "fixed",
  "fix_didnt_hold",
  "regressed",
  "still_open",
  "not_rechecked",
  "accepted",
  "strength_lost",
] as const;
export type GateBucket = (typeof GATE_BUCKETS)[number];

export const NOT_RECHECKED_REASONS = [
  "scenario_not_run",
  "not_observed",
  "uncertain",
  "not_mentioned",
] as const;
export type NotRecheckedReason = (typeof NOT_RECHECKED_REASONS)[number];

export const GATE_SEVERITIES = ["critical", "major", "minor"] as const;
export type GateSeverity = (typeof GATE_SEVERITIES)[number];

const uuid = z.string().uuid();
const timestamp = z.string().datetime({ offset: true });

const severityCountsSchema = z
  .object({
    critical: z.number().int().min(0),
    major: z.number().int().min(0),
    minor: z.number().int().min(0),
  })
  .strict();
export type GateSeverityCounts = z.infer<typeof severityCountsSchema>;

export const gateConditionResultSchema = z
  .object({
    id: z.enum(GATE_CONDITION_IDS),
    metric: z.string(),
    filter: z.record(z.string(), z.unknown()),
    op: z.enum(["eq", "lte", "gte"]),
    threshold: z.number(),
    actual: z.number(),
    level: z.enum(GATE_LEVELS),
    outcome: z.enum(["ok", "warn", "error"]),
    summary: z.string(),
    /** Canonicals behind a failing count, for evidence links. */
    canonical_ids: z.array(uuid).optional(),
  })
  .strict();
export type GateConditionResult = z.infer<typeof gateConditionResultSchema>;

export const gateItemSchema = z
  .object({
    canonical_id: uuid,
    bucket: z.enum(GATE_BUCKETS),
    severity: z.enum(GATE_SEVERITIES).nullable(),
    type: z.string(),
    title: z.string(),
    scenario_id: uuid.nullable(),
    persona_id: uuid.nullable(),
    evidence: z
      .object({
        walkthrough_id: uuid.nullable(),
        step_id: uuid.nullable(),
        finding_id: uuid.nullable().optional(),
        seen: z.string().nullable(),
      })
      .strict(),
    /** not_rechecked only: why it was not re-checked. */
    reason: z.enum(NOT_RECHECKED_REASONS).optional(),
    /** new only: the scenario had no baseline run, so it cannot fail the gate. */
    no_baseline: z.boolean().optional(),
    /** accepted only: who accepted it and why. */
    accepted: z
      .object({
        state: z.string(),
        reason: z.string().nullable(),
        note: z.string().nullable(),
        by: uuid.nullable(),
        at: timestamp.nullable(),
      })
      .strict()
      .optional(),
    flaky: z.boolean(),
    url: z.string().nullable(),
  })
  .strict();
export type GateItem = z.infer<typeof gateItemSchema>;

export const gateVerdictDeltaSchema = z
  .object({
    scenario_id: uuid,
    human_test_id: z.string(),
    baseline: z.enum(["yes", "partially", "no"]).nullable(),
    candidate: z.enum(["yes", "partially", "no"]),
    delta: z.enum([
      "improved",
      "unchanged",
      "declined",
      "declined_to_no",
      "no_baseline",
    ]),
  })
  .strict();
export type GateVerdictDelta = z.infer<typeof gateVerdictDeltaSchema>;

export const gateResultSchema = z
  .object({
    $schema: z.literal(GATE_RESULT_SCHEMA_URL),
    schema_version: z.literal(1),
    project_id: uuid,
    evaluation_id: uuid,
    evaluated_at: timestamp,
    source: z.enum(["cloud", "local"]),
    evaluator_version: z.string(),
    status: z.enum(GATE_STATUSES),
    /** One-line human summary, e.g. the GitHub check title. */
    headline: z.string(),
    /** Explanations that qualify the status (no baseline, cross-environment…). */
    notes: z.array(z.string()),
    build: z
      .object({
        id: uuid.nullable(),
        label: z.string().nullable(),
        git_sha: z.string().nullable(),
        git_branch: z.string().nullable(),
        environment: z.string().nullable(),
        run_ids: z.array(uuid),
      })
      .strict(),
    baseline: z
      .object({
        mode: z.enum(BASELINE_MODES),
        build_id: uuid.nullable(),
        label: z.string().nullable(),
        as_of: timestamp.nullable(),
        environment: z.enum(["same", "any"]),
        per_scenario: z.array(
          z
            .object({
              scenario_id: uuid,
              baseline_run_id: uuid.nullable(),
              carried_forward: z.boolean(),
              environment: z.string().nullable().optional(),
            })
            .strict(),
        ),
      })
      .strict(),
    scope: z
      .object({
        kind: z.enum(["all", "capability"]),
        capabilities: z.array(z.string()),
        scenario_ids: z.array(uuid),
      })
      .strict(),
    coverage: z
      .object({
        scenarios: z
          .object({
            rerun: z.number().int().min(0),
            in_scope: z.number().int().min(0),
          })
          .strict(),
        human_tests: z
          .object({
            judged: z.number().int().min(0),
            in_scope: z.number().int().min(0),
          })
          .strict(),
        problems_rechecked: z
          .object({
            rechecked: z.number().int().min(0),
            baseline_open: z.number().int().min(0),
          })
          .strict(),
      })
      .strict(),
    counts: z
      .object({
        new: severityCountsSchema,
        fixed: severityCountsSchema,
        fix_didnt_hold: severityCountsSchema,
        regressed: severityCountsSchema,
        still_open: severityCountsSchema,
        not_rechecked: severityCountsSchema,
        accepted: severityCountsSchema,
        strengths_lost: z.number().int().min(0),
      })
      .strict(),
    verdicts: z
      .object({
        improved: z.number().int().min(0),
        unchanged: z.number().int().min(0),
        declined: z.number().int().min(0),
        declined_to_no: z.number().int().min(0),
      })
      .strict(),
    verdict_deltas: z.array(gateVerdictDeltaSchema),
    conditions: z.array(gateConditionResultSchema),
    items: z.array(gateItemSchema).max(GATE_ITEMS_CAP),
    items_truncated: z.boolean(),
    links: z
      .object({
        build_page: z.string().nullable(),
        check_run: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type GateResult = z.infer<typeof gateResultSchema>;
